import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import api, { fmtErr, isDriveEmployee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, ContactRound, FileDown, FileUp, Pencil, Phone, Search, Trash2, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export default function StudioManpower() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { askConfirm, confirmDialog } = useConfirm();
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null); // null | {} (new) | person
  const [form, setForm] = useState({ name: "", title: "", company: "", phone: "" });
  const photoInput = useRef(null);
  const cvInput = useRef(null);
  const importInput = useRef(null);
  const uploadFor = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const { data: people = [], isLoading } = useQuery({
    queryKey: ["studio-manpower"],
    queryFn: async () => (await api.get("/studio/manpower")).data,
  });
  const { data: companies = [] } = useQuery({
    queryKey: ["studio-companies"],
    queryFn: async () => (await api.get("/studio/companies")).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["studio-manpower"] });

  const saveMut = useMutation({
    mutationFn: async () => (editTarget?.id ? api.put(`/studio/manpower/${editTarget.id}`, form) : api.post("/studio/manpower", form)),
    onSuccess: () => {
      toast.success(t(editTarget?.id ? "studio.toastUpdated" : "studio.toastAdded"));
      setEditTarget(null);
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/studio/manpower/${id}`),
    onSuccess: () => { toast.success(t("studio.toastDeleted")); invalidate(); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const fileMut = useMutation({
    mutationFn: async ({ id, kind, file }) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post(`/studio/manpower/${id}/${kind}`, fd);
    },
    onSuccess: (_d, v) => { toast.success(t(v.kind === "photo" ? "studio.photoUpdated" : "studio.cvUpdated")); invalidate(); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const importMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/studio/manpower/import", fd)).data;
    },
    onSuccess: (d) => { setImportResult(d); invalidate(); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const downloadTemplate = () => {
    const csv = "name,title,company,phone\nJane Doe,Technician,PRUVA,+971500000000\n";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv" }));
    a.download = "manpower_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return people;
    return people.filter((p) => [p.name, p.title, p.company].some((v) => (v || "").toLowerCase().includes(q)));
  }, [people, search]);

  if (!isDriveEmployee(user)) return <Navigate to="/" replace />;

  const openEdit = (p) => {
    setEditTarget(p || {});
    setForm(p?.id ? { name: p.name, title: p.title, company: p.company, phone: p.phone } : { name: "", title: "", company: "", phone: "" });
  };

  const pickFile = (id, kind) => {
    uploadFor.current = { id, kind };
    (kind === "photo" ? photoInput : cvInput).current?.click();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f && uploadFor.current) fileMut.mutate({ ...uploadFor.current, file: f });
  };

  return (
    <div className="space-y-6" data-testid="studio-manpower-page">
      <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="mp-photo-input" />
      <input ref={cvInput} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={onFile} data-testid="mp-cv-input" />
      <input ref={importInput} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) importMut.mutate(f); }} data-testid="mp-import-input" />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{t("studio.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("studio.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setImportResult(null); setImportOpen(true); }} data-testid="studio-import">
            <FileUp className="mr-1.5 h-4 w-4" /> {t("studio.importBtn")}
          </Button>
          <Button onClick={() => openEdit(null)} className="bg-slate-900 hover:bg-slate-800" data-testid="studio-add-person">
            <UserPlus className="mr-1.5 h-4 w-4" /> {t("studio.addPerson")}
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("studio.searchPh")} className="pl-9" data-testid="studio-search" />
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
      ) : filtered.length === 0 ? (
        <p className="rounded-lg border border-dashed py-12 text-center text-sm text-slate-400" data-testid="studio-empty">{t("studio.noPeople")}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((p) => (
            <div key={p.id} className="rounded-lg border bg-white p-4" data-testid={`mp-card-${p.id}`}>
              <div className="flex items-start gap-3">
                {p.has_photo ? (
                  <img src={`${API}/studio/manpower/${p.id}/photo?v=${encodeURIComponent(p.updated_at || "")}`} alt={p.name} className="h-12 w-12 shrink-0 rounded-full border object-cover" data-testid={`mp-photo-img-${p.id}`} />
                ) : (
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-slate-800 font-mono text-sm font-semibold text-white" data-testid={`mp-initials-${p.id}`}>{initials(p.name)}</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900">{p.name}</div>
                  {p.title && <div className="truncate text-xs font-medium text-sky-700">{p.title}</div>}
                  {p.company && <div className="mt-0.5 truncate text-xs text-slate-500">{p.company}</div>}
                  {p.phone && <div className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-slate-400"><Phone className="h-3 w-3" />{p.phone}</div>}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 border-t pt-2.5">
                <Button variant="ghost" size="icon" title={t("studio.editPerson")} onClick={() => openEdit(p)} data-testid={`mp-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" title={t("studio.photoL")} onClick={() => pickFile(p.id, "photo")} data-testid={`mp-photo-upload-${p.id}`}><Camera className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" title={t("studio.uploadCv")} onClick={() => pickFile(p.id, "cv")} data-testid={`mp-cv-upload-${p.id}`}><FileUp className="h-4 w-4" /></Button>
                {p.has_cv && (
                  <Button variant="ghost" size="icon" title={p.cv_filename || "CV"} onClick={() => window.open(`${API}/studio/manpower/${p.id}/cv`, "_blank")} data-testid={`mp-cv-download-${p.id}`}><FileDown className="h-4 w-4 text-emerald-600" /></Button>
                )}
                <Button variant="ghost" size="icon" className="ml-auto text-rose-600" title={t("common.delete")} onClick={() => askConfirm(t("studio.deleteConfirm", { name: p.name }), () => deleteMut.mutate(p.id))} data-testid={`mp-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="mp-dialog">
          <DialogHeader><DialogTitle>{t(editTarget?.id ? "studio.editPerson" : "studio.addPerson")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("studio.nameL")} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mp-name" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("studio.titleL")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("studio.titlePh")} data-testid="mp-title" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("studio.companyL")}</Label>
              <Input list="mp-companies" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t("studio.companyPh")} data-testid="mp-company" />
              <datalist id="mp-companies">
                {companies.map((c) => <option key={c.id} value={c.code ? `${c.code} — ${c.name}` : c.name} />)}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label>{t("studio.phoneL")}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="mp-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} data-testid="mp-cancel">{t("common.cancel")}</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name.trim() || saveMut.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="mp-save">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="mp-import-dialog">
          <DialogHeader><DialogTitle>{t("studio.importT")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{t("studio.importHint")}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="mp-import-template">{t("studio.downloadTemplate")}</Button>
              <Button size="sm" onClick={() => importInput.current?.click()} disabled={importMut.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="mp-import-choose">
                {importMut.isPending ? t("common.loading") : t("studio.importBtn")}
              </Button>
            </div>
            {importResult && (
              <p className="rounded-md border bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="mp-import-result">
                {t("studio.importResultT", { created: importResult.created, skipped: importResult.skipped })}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </div>
  );
}
